import { EncabezadoEmpresa } from "@/componentes/empresa/EncabezadoEmpresa"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { comoFecha } from "@/lib/fechas"

import { TablaEstructura } from "./TablaEstructura"
import type { DocumentoManual, NodoManual, ProcesoManual } from "./tipos"
import { numeral, porPadre } from "./tipos"

/**
 * El manual de organización, listo para leer y para imprimir.
 *
 * ⚠ **Esto no redacta nada: transcribe.** Cada línea sale de la tabla donde ya vive
 * —el objetivo de un proceso, la meta de un objetivo, el cargo de una posición—, y por
 * eso el manual no se puede quedar desactualizado sin que el sistema entero lo esté.
 * Un manual que se escribe aparte es un manual que a los seis meses miente.
 *
 * ⚠ **La estructura se enseña como una lista con sangría, no como el lienzo.** En
 * papel, un organigrama dibujado a lo ancho no cabe: o se reduce hasta ser ilegible o
 * se parte entre hojas por la mitad de una rama. La lista con sangría dice lo mismo
 * —quién cuelga de quién— y se lee en A4. El lienzo está en su propia pantalla.
 *
 * ⚠ **Los procesos van como TABLAS de procedimiento.** Es su forma en un manual: cada
 * paso una fila, con su responsable al lado. El diagrama es para la pantalla.
 */

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2>{titulo}</h2>
      {children}
    </section>
  )
}

function Rama({
  nodo,
  hijos,
  nivel,
  porId,
}: {
  nodo: NodoManual
  hijos: Map<number | null, NodoManual[]>
  nivel: number
  porId: Map<number, NodoManual>
}) {
  const suyos = hijos.get(nodo.id) ?? []
  const tambien = nodo.reporta_tambien_a
    .map((id) => porId.get(id)?.cargo)
    .filter((c): c is string => Boolean(c))

  return (
    <li style={{ marginLeft: `${nivel * 18}px` }} className="mt-1">
      <span className="font-semibold">{nodo.cargo ?? "(sin cargo)"}</span>
      {nodo.departamento && (
        <span style={{ color: "var(--fg-muted)" }}> · {nodo.departamento}</span>
      )}
      <span>
        {" — "}
        {/* La palabra «Vacante» y no un hueco: una caja en blanco se lee como un
            error de impresión. */}
        {nodo.vacante ? <em>Vacante</em> : nodo.ocupante}
      </span>
      {tambien.length > 0 && (
        <span className="text-sm" style={{ color: "var(--fg-muted)" }}>
          {" "}
          (también reporta a: {tambien.join(", ")})
        </span>
      )}
      {suyos.length > 0 && (
        <ul>
          {suyos.map((hijo) => (
            <Rama key={hijo.id} nodo={hijo} hijos={hijos} nivel={nivel + 1} porId={porId} />
          ))}
        </ul>
      )}
    </li>
  )
}

function TablaProceso({ proceso }: { proceso: ProcesoManual }) {
  return (
    <Tabla>
      <Cabecera>
        <Th derecha>N.º</Th>
        <Th>Actividad</Th>
        <Th>Responsable</Th>
        <Th>Salidas de la decisión</Th>
      </Cabecera>
      <Cuerpo>
        {proceso.pasos.map((paso, i) => (
          <Fila key={`${paso.orden}-${paso.titulo}`}>
            <Td derecha numero>
              {i + 1}
            </Td>
            <Td>
              <span className="font-medium">{paso.titulo}</span>
              {paso.descripcion && (
                <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                  {paso.descripcion}
                </span>
              )}
            </Td>
            <Td>{paso.responsable ?? "—"}</Td>
            <Td>
              {paso.rama_si || paso.rama_no
                ? `Sí: ${paso.rama_si ?? "—"} · No: ${paso.rama_no ?? "—"}`
                : "—"}
            </Td>
          </Fila>
        ))}
      </Cuerpo>
    </Tabla>
  )
}

export function ManualDocumento({ doc }: { doc: DocumentoManual }) {
  const { identidad, estrategia, organigramas, procesos } = doc

  return (
    <article className="doc-prosa">
      {/* ⚠ El membrete sale del MISMO componente que la vista previa de la
          configuración y que los documentos del SGI. Repetirlo aquí con el logo y el
          RIF a mano es como acaban los sistemas donde cada plantilla lleva su copia:
          cambiar el logo obliga a repasarlas todas. */}
      <EncabezadoEmpresa titulo="Manual de Organización" fecha={doc.generado_en} />

      <header className="text-center">
        <p className="text-lg font-semibold">{identidad.razon_social || identidad.nombre}</p>
        <p className="mt-1 text-xs tabular" style={{ color: "var(--fg-muted)" }}>
          Generado el {comoFecha(doc.generado_en)}
        </p>
      </header>

      <Seccion titulo="Identidad de la organización">
        <dl>
          {identidad.direccion && (
            <>
              <dt className="font-semibold">Dirección</dt>
              <dd>{identidad.direccion}</dd>
            </>
          )}
          {identidad.telefono && (
            <>
              <dt className="font-semibold">Teléfono</dt>
              <dd className="tabular">{identidad.telefono}</dd>
            </>
          )}
          {identidad.email && (
            <>
              <dt className="font-semibold">Correo</dt>
              <dd>{identidad.email}</dd>
            </>
          )}
        </dl>
        {identidad.vision && (
          <>
            <h3>Visión</h3>
            <p>{identidad.vision}</p>
          </>
        )}
        {identidad.mision && (
          <>
            <h3>Misión</h3>
            <p>{identidad.mision}</p>
          </>
        )}
        {identidad.valores.length > 0 && (
          <>
            <h3>Valores</h3>
            <ul>
              {identidad.valores.map((v) => (
                <li key={v}>{v}</li>
              ))}
            </ul>
          </>
        )}
      </Seccion>

      {(doc.estructura?.length ?? 0) > 0 && (
        <Seccion titulo="Estructura organizacional">
          <TablaEstructura areas={doc.estructura ?? []} />
        </Seccion>
      )}

      {estrategia.pilares.length > 0 && (
        <Seccion titulo="Despliegue estratégico">
          <p>
            Avance global: <strong className="tabular">{estrategia.avance} %</strong>
          </p>
          {estrategia.pilares.map((pilar) => (
            <div key={`${pilar.origen}-${pilar.titulo}`}>
              <h3>
                {pilar.titulo}{" "}
                <span className="text-sm" style={{ color: "var(--fg-muted)" }}>
                  ({pilar.origen.toLowerCase()} · {pilar.avance} %)
                </span>
              </h3>
              {pilar.descripcion && <p>{pilar.descripcion}</p>}
              {pilar.objetivos.length > 0 && (
                <Tabla>
                  <Cabecera>
                    <Th>Objetivo</Th>
                    <Th>Indicador</Th>
                    <Th derecha>Meta</Th>
                    <Th>Responsable</Th>
                    <Th derecha>Avance</Th>
                  </Cabecera>
                  <Cuerpo>
                    {pilar.objetivos.map((o) => (
                      <Fila key={o.enunciado}>
                        <Td>{o.enunciado}</Td>
                        <Td>{o.indicador ?? "—"}</Td>
                        <Td derecha numero>
                          {o.meta ? `${o.meta} ${o.unidad ?? ""}`.trim() : "—"}
                        </Td>
                        <Td>{o.responsable ?? "—"}</Td>
                        <Td derecha numero>
                          {o.avance} %
                        </Td>
                      </Fila>
                    ))}
                  </Cuerpo>
                </Tabla>
              )}
            </div>
          ))}
        </Seccion>
      )}

      {organigramas.map((mapa) => {
        const hijos = porPadre(mapa.nodos)
        const porId = new Map(mapa.nodos.map((n) => [n.id, n]))
        return (
          <Seccion key={mapa.nombre} titulo={`Estructura — ${mapa.nombre}`}>
            {mapa.descripcion && <p>{mapa.descripcion}</p>}
            {mapa.nodos.length === 0 ? (
              <p style={{ color: "var(--fg-muted)" }}>Este organigrama no tiene posiciones.</p>
            ) : (
              <ul style={{ listStyle: "none", marginLeft: 0 }}>
                {(hijos.get(null) ?? []).map((raiz) => (
                  <Rama key={raiz.id} nodo={raiz} hijos={hijos} nivel={0} porId={porId} />
                ))}
              </ul>
            )}
          </Seccion>
        )
      })}

      {procesos.length > 0 && (
        <Seccion titulo="Procesos">
          {procesos.map((proceso, i) => (
            <div key={proceso.nombre}>
              <h3>
                {numeral(i, proceso.es_transversal)}. {proceso.nombre}
              </h3>
              {proceso.objetivo && <p>{proceso.objetivo}</p>}
              {proceso.responsable && (
                <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
                  Responsable del proceso: {proceso.responsable}
                </p>
              )}
              <TablaProceso proceso={proceso} />
            </div>
          ))}
        </Seccion>
      )}
    </article>
  )
}
